import { maxBreakpoints, minBreakpoints, theme } from '@noths/polaris-client-ribbons-base';
import { mediaQuery, pxToRem, shadowUp } from '@noths/polaris-client-styles';
import { css } from 'styled-components';

const { spacing } = theme;

export const wrapper = css`
  margin: ${pxToRem(spacing.xxl)} 0 ${pxToRem(80)};

  ${mediaQuery(
    {
      max: maxBreakpoints.M,
    },
    css`
      margin-top: ${pxToRem(spacing.md)};
    `,
  )}
`;

export const breadcrumbContainer = css`
  grid-column: 1 / -1;

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 2 / -2;
    `,
  )}
`;

export const similarProductShelf = css`
  grid-column: 1 / -1;

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 2 / -2;
    `,
  )}
`;

export const similarProductInnerContainer = css`
  position: relative;
  z-index: 1;
  box-shadow: 0 ${pxToRem(2)} ${pxToRem(4)} 0 rgba(0, 0, 0, 0.2);
  margin-top: -${pxToRem(spacing.md)};

  ${/* A pseudo-element to hide the top box-shadow. */ ''}
  &:before {
    content: '';
    height: ${pxToRem(2)};
    top: -${pxToRem(2)};
    width: 100%;
    position: absolute;
    background: var(--neutral-white-base);
    z-index: 1;
  }

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      margin-top: -${pxToRem(spacing.xxl)};
      margin-bottom: ${pxToRem(42)};
    `,
  )}
`;

export const recommendedProductsContainer = css`
  &[data-hide-placement='true'] {
    margin-bottom: ${pxToRem(spacing.md)};

    ${mediaQuery(
      { min: minBreakpoints.L },
      css`
        margin-bottom: ${pxToRem(spacing.xxxxl)};
      `,
    )}
  }
`;

/*
  Active products placement needs additional margin vs the other placements due to partner storefront spacing/negative
  margins.
*/
export const activeProductsContainer = css`
  margin-bottom: ${pxToRem(spacing.xxxxl + spacing.md)};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      margin-bottom: ${pxToRem(spacing.xxxxl)};
    `,
  )}

  &[data-hide-placement='true'] {
    margin-bottom: ${pxToRem(spacing.xxxxl + spacing.md)};

    ${mediaQuery(
      { min: minBreakpoints.L },
      css`
        margin-bottom: ${pxToRem(spacing.xxxxl * 2 + 4)};
      `,
    )}
  }
`;

export const recommendationCarousel = css`
  grid-column: span 12;
  border-bottom: ${pxToRem(1)} solid var(--neutral-black-30);

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      margin-bottom: ${pxToRem(80)};
      grid-column: 2 / -2;
    `,
  )}
`;

export const productReviewsOuterWrapper = css`
  margin-top: ${pxToRem(spacing.xxl)};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      margin-top: ${pxToRem(spacing.xxxxl)};
    `,
  )}
`;

export const linkCarouselWrapper = css`
  grid-column: span 12;

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 2 / -2;
    `,
  )}
`;

export const productDescriptionWrapper = css`
  grid-column: span 12;

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 2 / -2;
    `,
  )}
`;

export const productReviewsWrapper = css`
  min-width: 0;
  grid-column: 1 / -1;
  grid-row: 2;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      grid-row: 1;
      grid-column: span 6;
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 2 / span 7;
    `,
  )}
`;

export const overallRatingWrapper = css`
  grid-row: 1;
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      grid-column: span 6;
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 9 / span 3;
    `,
  )}
`;

export const productDescriptionContainer = css`
  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      ${shadowUp};
    `,
  )};
`;

export const imagesContainer = css`
  ${/* min-width needed to fix bug in firefox where grid item overflows */ ''}
  min-width: 0;
  grid-column: 1 / -1;

  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      position: sticky;
      top: 0;
      z-index: -1;
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      grid-column: span 6;
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 2 / span 7;
    `,
  )}
`;

export const productInfoContainer = css`
  grid-column: 1 / -1;
  z-index: 2;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      grid-column: span 6;
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column: 9 / span 3;
    `,
  )}
`;

export const imagesAndInfoWrapper = css`
  ${
    /* Note: This line below creates a new stacking context to ensure that the product info is on top of 
       the product carousel when trying to achieve a parralax effect with fixed positioning. This new stacking
       context here however means we cannot have fixed positioned elements inside the product info organism */ ''
  }
  isolation: isolate;

  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      row-gap: 0;
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      margin-bottom: ${pxToRem(spacing.xxl)};
    `,
  )}
`;

export const storefrontWrapper = css`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background-color: var(--neutral-black-10);
  margin-bottom: ${pxToRem(48)};

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      margin-bottom: ${pxToRem(176)};
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      margin-bottom: ${pxToRem(212)};
    `,
  )}
`;
